/**
 * The problem:
 * 1. The analyses table only shows what `/offer-item-list` returns, i.e. what can be ordered now.
 * 2. An offer item can drop out of that list (remaining quantity 0, superseded, disabled) while
 *    still being on a sample saved earlier: we call it "unlisted".
 * 3. With no row in the table, the customer cannot uncheck it. If it is a stability analysis,
 *    it also keeps every other analysis disabled, so no replacement can be picked.
 *
 * The fix: for the selected samples, the unlisted offer items are added on top of the table
 * ("pinned", flagged `unlisted: true`). They can be removed, and re-added only to the samples
 * that had them when the page was loaded: they can never be ordered anew.
 *
 */
const wasOnSample = (unlisted, sampleId, offerItemId) => Boolean(
  unlisted[sampleId] && unlisted[sampleId].has(offerItemId),
);

const canAddTo = (unlisted, sampleId, row) => !row.unlisted
  || wasOnSample(unlisted, sampleId, row.id);

export default {
  /** The analysis ids to ask `/offer-item-list` about, each once. */
  analysisIdsOf(orderAnalyses = []) {
    const ids = orderAnalyses
      .flatMap(({ offerItems = [] }) => offerItems.map(item => item.analysisId));
    return [...new Set(ids)];
  },

  /**
   * @param orderAnalyses the `GET /v2/orders/{id}/sample-analyses` payload
   * @param listedIds ids of the offer items `/offer-item-list` returned
   * @returns per sample id, its offer items missing from `listedIds` (samples without any are
   * left out)
   */
  findUnlisted(orderAnalyses = [], listedIds = []) {
    const listed = new Set(listedIds);
    return orderAnalyses.reduce((unlisted, { sampleId, offerItems = [] }) => {
      const items = offerItems.filter(item => !listed.has(item.id));
      if (items.length) {
        // eslint-disable-next-line no-param-reassign
        unlisted[sampleId] = new Map(items.map(item => [item.id, item]));
      }
      return unlisted;
    }, {});
  },

  canAddTo,

  canAddToAny(unlisted, sampleIds, row) {
    return !row.unlisted || sampleIds.some(sampleId => wasOnSample(unlisted, sampleId, row.id));
  },

  /**
   * The unlisted offer items of the selected samples, as rows flagged `unlisted`.
   * Each item appears once, and not at all if `tableRows` already shows it.
   */
  rowsToPin(unlisted, selectedSampleIds = [], tableRows = []) {
    const shown = new Set(tableRows.map(row => row.id));
    const pinned = new Map();
    selectedSampleIds.forEach((sampleId) => {
      (unlisted[sampleId] || new Map()).forEach((item, id) => {
        if (!shown.has(id) && !pinned.has(id)) pinned.set(id, { ...item, unlisted: true });
      });
    });
    return [...pinned.values()];
  },
};
